import axios from 'axios';

/**
 * API Service for the Creative Review Modal Vue component.
 *
 * Centralizes all API calls for creative review, approvals, change requests,
 * and denials - replacing Livewire component functionality.
 *
 * @example
 * import CreativeTableApi from '@js/services/CreativeTableApi';
 *
 * // Get creative for carousel
 * const response = await CreativeTableApi.getByIds([1, 2, 3]);
 *
 * // Submit change request
 * await CreativeTableApi.submitChangeRequest(creativeId, {
 *     status_reason: 'Please update the logo',
 *     status_notes: 'The logo is too small'
 * });
 */
const CreativeTableApi = {
    /**
     * Get a single creative by ID.
     *
     * @param {number} creativeId - Creative ID
     * @returns {Promise<AxiosResponse>}
     */
    getCreative(creativeId) {
        return axios.get(`/api/creative-table/${creativeId}`);
    },

    /**
     * Get multiple creative items by IDs (for carousel).
     * Returns deduplicated creative based on checksum.
     *
     * @param {number[]} creativeIds - Array of creative IDs
     * @returns {Promise<AxiosResponse>}
     */
    getByIds(creativeIds) {
        return axios.post('/api/creative-table/by-ids', {
            creative_ids: creativeIds,
        });
    },

    /**
     * Update creative status (approve, deny, etc).
     *
     * @param {number} creativeId - Creative ID
     * @param {Object} data - Status update data
     * @param {string} data.status - New status (e.g., 'approved', 'denied')
     * @param {string} [data.status_reason] - Reason for status change
     * @param {string} [data.status_notes] - Additional notes
     * @param {number[]} [data.creative_ids] - IDs for batch operation on matching creative
     * @returns {Promise<AxiosResponse>}
     */
    updateStatus(creativeId, data) {
        return axios.put(`/api/creative-table/${creativeId}/status`, data);
    },

    /**
     * Approve creative and optionally move to next in carousel.
     *
     * @param {number} creativeId - Creative ID
     * @param {number[]} [creativeIds] - All creative IDs for batch operation
     * @returns {Promise<AxiosResponse>}
     */
    approve(creativeId, creativeIds = []) {
        return this.updateStatus(creativeId, {
            status: 'approved',
            creative_ids: creativeIds,
        });
    },

    /**
     * Submit a change request for creative.
     *
     * @param {number} creativeId - Creative ID
     * @param {Object} data - Change request data
     * @param {string} data.status_reason - Reason for change request (required)
     * @param {string} [data.status_notes] - Additional notes
     * @returns {Promise<AxiosResponse>}
     */
    submitChangeRequest(creativeId, data) {
        return axios.post(`/api/creative-table/${creativeId}/change-request`, data);
    },

    /**
     * Deny creative.
     *
     * @param {number} creativeId - Creative ID
     * @param {Object} data - Denial data
     * @param {string} data.status_reason - Reason for denial (required)
     * @param {string} [data.status_notes] - Additional notes
     * @param {number[]} data.creative_ids - All creative IDs to deny
     * @returns {Promise<AxiosResponse>}
     */
    deny(creativeId, data) {
        return axios.post(`/api/creative-table/${creativeId}/deny`, data);
    },

    /**
     * Apply a change to creative (for advertisers updating their creative).
     *
     * @param {number} creativeId - Creative ID
     * @param {Object} data - Update data
     * @param {string} [data.text] - New text content (for text creative)
     * @returns {Promise<AxiosResponse>}
     */
    applyChange(creativeId, data = {}) {
        return axios.post(`/api/creative-table/${creativeId}/apply-change`, data);
    },

    /**
     * Deny an entire order (denies all creative in the order).
     *
     * @param {number} orderId - Order ID
     * @param {Object} data - Denial data
     * @param {string} data.status_reason - Reason for denial (required)
     * @param {string} [data.status_notes] - Additional notes
     * @returns {Promise<AxiosResponse>}
     */
    denyOrder(orderId, data) {
        return axios.post(`/api/creative-table/orders/${orderId}/deny`, data);
    },

    /**
     * Check if all creative in an order have been reviewed.
     *
     * @param {number} orderId - Order ID
     * @returns {Promise<AxiosResponse>}
     */
    checkOrderComplete(orderId) {
        return axios.get(`/api/creative-table/orders/${orderId}/check-complete`);
    },

    /**
     * Upload replacement media for creative.
     * Uses the existing creative media upload endpoint.
     *
     * @param {number} creativeId - Creative ID
     * @param {FormData} formData - Form data containing the file
     * @returns {Promise<AxiosResponse>}
     */
    uploadMedia(creativeId, formData) {
        return axios.put(`/api/creative/${creativeId}/media`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
};

export default CreativeTableApi;
